import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  Alert,
  Grid,
  Slider,
  TextField,
  CircularProgress,
  Divider,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { SketchPicker } from 'react-color';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteIcon from '@mui/icons-material/Delete';
import { testColor, testMovieQuote, getSavedLightState, saveLightState, restoreLightState, clearSavedLightState } from '../services/api';

function Test() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [color, setColor] = useState({ r: 255, g: 100, b: 100 });
  const [brightness, setBrightness] = useState(200);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);

  // Movie quote testing state
  const [quoteMovie, setQuoteMovie] = useState('');
  const [quoteYear, setQuoteYear] = useState('');
  const [quoteResult, setQuoteResult] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(null);

  // Light state save/restore
  const [savedState, setSavedState] = useState(null);
  const [stateLoading, setStateLoading] = useState(false);
  const [stateMessage, setStateMessage] = useState(null);

  // Load saved state info on mount
  useEffect(() => {
    loadSavedStateInfo();
  }, []);

  const loadSavedStateInfo = async () => {
    try {
      const response = await getSavedLightState();
      if (response.data.hasSavedState) {
        setSavedState(response.data.state);
      } else {
        setSavedState(null);
      }
    } catch (err) {
      console.error('Failed to load saved state info:', err);
    }
  };

  const handleSaveState = async (force = false) => {
    try {
      setStateLoading(true);
      setStateMessage(null);
      const response = await saveLightState(force);
      if (response.data.alreadySaved) {
        setStateMessage({ type: 'info', text: 'Initial state was already saved. Use "Save New" to overwrite.' });
      } else {
        setStateMessage({ type: 'success', text: `Saved state of ${response.data.lightCount} lights!` });
      }
      await loadSavedStateInfo();
    } catch (err) {
      setStateMessage({ type: 'error', text: 'Failed to save state: ' + err.message });
    } finally {
      setStateLoading(false);
    }
  };

  const handleRestoreState = async () => {
    try {
      setStateLoading(true);
      setStateMessage(null);
      await restoreLightState();
      setStateMessage({ type: 'success', text: 'Lights restored to initial state!' });
    } catch (err) {
      setStateMessage({ type: 'error', text: err.response?.data?.error || 'Failed to restore state' });
    } finally {
      setStateLoading(false);
    }
  };

  const handleClearState = async () => {
    try {
      setStateLoading(true);
      setStateMessage(null);
      await clearSavedLightState();
      setSavedState(null);
      setStateMessage({ type: 'success', text: 'Saved state cleared. A new initial state will be captured on next theme apply.' });
    } catch (err) {
      setStateMessage({ type: 'error', text: 'Failed to clear state: ' + err.message });
    } finally {
      setStateLoading(false);
    }
  };

  // Convert RGB to XY for Hue
  const rgbToXY = (r, g, b) => {
    // Normalize
    let red = r / 255.0;
    let green = g / 255.0;
    let blue = b / 255.0;

    // Gamma correction
    red = (red > 0.04045) ? Math.pow((red + 0.055) / 1.055, 2.4) : (red / 12.92);
    green = (green > 0.04045) ? Math.pow((green + 0.055) / 1.055, 2.4) : (green / 12.92);
    blue = (blue > 0.04045) ? Math.pow((blue + 0.055) / 1.055, 2.4) : (blue / 12.92);

    // Convert to XYZ
    const X = red * 0.664511 + green * 0.154324 + blue * 0.162028;
    const Y = red * 0.283881 + green * 0.668433 + blue * 0.047685;
    const Z = red * 0.000088 + green * 0.072310 + blue * 0.986039;

    const sum = X + Y + Z;
    if (sum === 0) return { x: 0, y: 0 };

    return {
      x: Math.max(0, Math.min(1, X / sum)),
      y: Math.max(0, Math.min(1, Y / sum)),
    };
  };

  const handleTestColor = async () => {
    try {
      setLoading(true);
      setError(null);

      const xy = rgbToXY(color.r, color.g, color.b);
      const hexColor = `#${color.r.toString(16).padStart(2, '0')}${color.g.toString(16).padStart(2, '0')}${color.b.toString(16).padStart(2, '0')}`;

      await testColor({
        rgb: color,
        hex: hexColor,
        xy: xy,
        brightness: brightness,
      });

      setSuccess('Color applied to lights!');
    } catch (err) {
      setError('Failed to apply color: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const presetColors = [
    { name: 'Warm White', rgb: { r: 255, g: 240, b: 200 } },
    { name: 'Cool Blue', rgb: { r: 100, g: 150, b: 255 } },
    { name: 'Energetic Red', rgb: { r: 255, g: 50, b: 50 } },
    { name: 'Calming Green', rgb: { r: 100, g: 255, b: 150 } },
    { name: 'Royal Purple', rgb: { r: 150, g: 50, b: 200 } },
    { name: 'Sunset Orange', rgb: { r: 255, g: 150, b: 50 } },
  ];

  const handlePresetClick = (preset) => {
    setColor(preset.rgb);
  };

  const handleTestQuote = async () => {
    if (!quoteMovie.trim()) {
      setQuoteError('Please enter a movie name');
      return;
    }

    try {
      setQuoteLoading(true);
      setQuoteError(null);
      setQuoteResult(null);

      const year = quoteYear ? parseInt(quoteYear) : null;
      const response = await testMovieQuote(quoteMovie, year);
      setQuoteResult(response.data);
    } catch (err) {
      setQuoteError('Failed to fetch quote: ' + (err.response?.data?.error || err.message));
    } finally {
      setQuoteLoading(false);
    }
  };

  const sampleMovies = [
    'Star Wars',
    'The Godfather',
    'Forrest Gump',
    'The Terminator',
    'Titanic',
    'The Wizard of Oz',
  ];

  return (
    <Box>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
      >
        Test Colors
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Preview colors on your lights before applying movie themes.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Color Picker
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, overflow: 'auto' }}>
              <SketchPicker
                color={color}
                onChange={(newColor) => setColor(newColor.rgb)}
                width={isMobile ? 200 : 220}
              />
            </Box>

            <Typography variant="subtitle2" gutterBottom>
              Brightness
            </Typography>
            <Slider
              value={brightness}
              onChange={(e, newValue) => setBrightness(newValue)}
              min={1}
              max={254}
              valueLabelDisplay="auto"
              sx={{ mb: 3 }}
            />

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                mb: 2
              }}
            >
              <TextField
                label="Red"
                type="number"
                value={color.r}
                onChange={(e) => setColor({ ...color, r: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 0, max: 255 }}
                fullWidth
                size={isMobile ? "small" : "medium"}
              />
              <TextField
                label="Green"
                type="number"
                value={color.g}
                onChange={(e) => setColor({ ...color, g: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 0, max: 255 }}
                fullWidth
                size={isMobile ? "small" : "medium"}
              />
              <TextField
                label="Blue"
                type="number"
                value={color.b}
                onChange={(e) => setColor({ ...color, b: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 0, max: 255 }}
                fullWidth
                size={isMobile ? "small" : "medium"}
              />
            </Box>

            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={<PlayArrowIcon />}
              onClick={handleTestColor}
              disabled={loading}
            >
              Apply to Lights
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Preset Colors
            </Typography>
            <Grid container spacing={2}>
              {presetColors.map((preset) => (
                <Grid item xs={6} key={preset.name}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => handlePresetClick(preset)}
                    sx={{
                      height: { xs: 60, sm: 80 },
                      backgroundColor: `rgb(${preset.rgb.r}, ${preset.rgb.g}, ${preset.rgb.b})`,
                      color: 'white',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      '&:hover': {
                        backgroundColor: `rgb(${preset.rgb.r}, ${preset.rgb.g}, ${preset.rgb.b})`,
                        opacity: 0.8,
                      },
                    }}
                  >
                    {preset.name}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Paper>

          <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Current Color Preview
            </Typography>
            <Box
              sx={{
                width: '100%',
                height: { xs: 150, sm: 200 },
                backgroundColor: `rgb(${color.r}, ${color.g}, ${color.b})`,
                borderRadius: 2,
                border: '2px solid rgba(255,255,255,0.1)',
              }}
            />
            <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
              RGB({color.r}, {color.g}, {color.b})
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
              Brightness: {brightness}/254
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Light State Save/Restore Section */}
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2.125rem' }
        }}
      >
        <RestoreIcon />
        Save & Restore Light State
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Save your current light settings before applying movie themes, then restore them later.
        The initial state is automatically saved when you apply your first movie theme.
      </Typography>

      {stateMessage && (
        <Alert severity={stateMessage.type} sx={{ mb: 2 }} onClose={() => setStateMessage(null)}>
          {stateMessage.text}
        </Alert>
      )}

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Saved State
            </Typography>
            {savedState ? (
              <Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  <Chip
                    label={`${savedState.lightCount} lights`}
                    color="primary"
                    size="small"
                  />
                  <Chip
                    label={`Saved: ${new Date(savedState.savedAt).toLocaleString()}`}
                    variant="outlined"
                    size="small"
                  />
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 2,
                    wordBreak: 'break-word'
                  }}
                >
                  Lights: {savedState.lights?.map(l => l.name).join(', ')}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No saved state. Apply a movie theme to automatically save the initial state,
                or manually save the current state.
              </Typography>
            )}

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                flexWrap: 'wrap'
              }}
            >
              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={() => handleSaveState(false)}
                disabled={stateLoading}
                fullWidth={isMobile}
              >
                Save Current
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<SaveIcon />}
                onClick={() => handleSaveState(true)}
                disabled={stateLoading}
                fullWidth={isMobile}
              >
                Save New
              </Button>
              {savedState && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleClearState}
                  disabled={stateLoading}
                  fullWidth={isMobile}
                >
                  Clear
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Restore Lights
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Restore your lights to the saved initial state. This will revert any movie themes
              you've applied back to how your lights were before.
            </Typography>
            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={stateLoading ? <CircularProgress size={20} color="inherit" /> : <RestoreIcon />}
              onClick={handleRestoreState}
              disabled={stateLoading || !savedState}
            >
              {stateLoading ? 'Restoring...' : 'Restore Initial State'}
            </Button>
          </Paper>
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Movie Quote Testing Section */}
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2.125rem' }
        }}
      >
        <FormatQuoteIcon />
        Test Movie Quotes
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Test what famous quote would be announced for a movie.
      </Typography>

      {quoteError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setQuoteError(null)}>
          {quoteError}
        </Alert>
      )}

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Enter Movie
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                mb: 2
              }}
            >
              <TextField
                label="Movie Title"
                value={quoteMovie}
                onChange={(e) => setQuoteMovie(e.target.value)}
                placeholder="Star Wars"
                fullWidth
                onKeyPress={(e) => e.key === 'Enter' && handleTestQuote()}
                size={isMobile ? "small" : "medium"}
              />
              <TextField
                label="Year (optional)"
                value={quoteYear}
                onChange={(e) => setQuoteYear(e.target.value)}
                placeholder="1977"
                sx={{ width: { xs: '100%', sm: 150 } }}
                type="number"
                size={isMobile ? "small" : "medium"}
              />
            </Box>
            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={quoteLoading ? <CircularProgress size={20} color="inherit" /> : <FormatQuoteIcon />}
              onClick={handleTestQuote}
              disabled={quoteLoading || !quoteMovie.trim()}
            >
              {quoteLoading ? 'Fetching Quote...' : 'Get Famous Quote'}
            </Button>

            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
              Try these movies:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {sampleMovies.map((movie) => (
                <Button
                  key={movie}
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setQuoteMovie(movie);
                    setQuoteResult(null);
                  }}
                  sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}
                >
                  {movie}
                </Button>
              ))}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, minHeight: { xs: 200, sm: 250 } }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Result
            </Typography>
            {quoteResult ? (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Movie: {quoteResult.movie} {quoteResult.year ? `(${quoteResult.year})` : ''}
                </Typography>
                {quoteResult.quote ? (
                  <>
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        borderRadius: 2,
                        position: 'relative',
                      }}
                    >
                      <FormatQuoteIcon sx={{ position: 'absolute', top: 8, left: 8, opacity: 0.3 }} />
                      <Typography variant="h6" sx={{ pl: 4, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        "{quoteResult.quote}"
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      <strong>Google Home will say:</strong>
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1, fontStyle: 'italic', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      "{quoteResult.fullResponse}"
                    </Typography>
                  </>
                ) : (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {quoteResult.message || 'No quote found for this movie.'}
                  </Alert>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: { xs: 120, sm: 180 },
                  color: 'text.secondary',
                }}
              >
                <Typography variant="body2" sx={{ textAlign: 'center' }}>
                  Enter a movie title and click "Get Famous Quote" to test
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Test;
